import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RoleState } from '../../../types/ndisSection/manageRolesTypes';
import axiosInstance from 'Services/APIService';

const initialState: RoleState = {
  roles: [],
  loading: false,
  error: null
};

// Async thunks for API calls
export const fetchRoles = createAsyncThunk('roles/fetchRoles', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get('/role/get-role-list');
    console.log('Fetch roles response:', response.data);
    return response.data.roles;
  } catch (error: any) {
    console.error('Fetch roles error:', error);
    return rejectWithValue(error.response?.data?.error || 'Failed to fetch roles');
  }
});

export const createRole = createAsyncThunk(
  'roles/createRole',
  async (
    { label, isMasterRole }: { label: string; isMasterRole: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/role/create-role', { label, isMasterRole });
      console.log('Create role response:', response.data);
      // if (!response.data.role) {
      //   throw new Error('No role data in response');
      // }
      return response.data;
    } catch (error: any) {
      console.error('Create role error:', error);
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to create role'
      );
    }
  }
);

export const updateRole = createAsyncThunk(
  'roles/updateRole',
  async (
    { role_id, label, isMasterRole }: { role_id: string; label: string; isMasterRole: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/role/update-role', {
        role_id,
        label,
        isMasterRole
      });
      console.log('Update role response:', response.data);
      return { role_id, label, isMasterRole };
    } catch (error: any) {
      console.error('Update role error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to update role');
    }
  }
);

export const deleteRole = createAsyncThunk(
  'roles/deleteRole',
  async (role_id: string, { rejectWithValue }) => {
    try {
      await axiosInstance.post('/role/delete-role', { role_id });
      console.log('Delete role success:', role_id);
      return role_id;
    } catch (error: any) {
      console.error('Delete role error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to delete role');
    }
  }
);

const roleSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Roles
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload.map((role: any) => ({
          ...role,
          isMasterRole: role.isMasterRole ?? false
        }));
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Role
    builder
      .addCase(createRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles.push({
          ...action.payload,
          isMasterRole: action.payload.isMasterRole ?? false
        });
      })
      .addCase(createRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Role
    builder
      .addCase(updateRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.loading = false;
        const { role_id, label, isMasterRole } = action.payload;
        const index = state.roles.findIndex((role) => role.role_id === role_id);
        if (index !== -1) {
          state.roles[index] = {
            ...state.roles[index],
            label,
            isMasterRole,
            updated_at: new Date().toISOString(),
            updated_by: 'unknown'
          };
        }
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Role
    builder
      .addCase(deleteRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = state.roles.filter((role) => role.role_id !== action.payload);
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearError } = roleSlice.actions;
export default roleSlice.reducer;
